import { type ReactNode, useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';

interface ParallaxProps {
  children: ReactNode;
  /** Intensidade: quanto o elemento se move em relação ao scroll. */
  speed?: number;
  /** Classe CSS extra. */
  className?: string;
}

/**
 * Aplica efeito de parallax ao conteúdo baseado no scroll da janela.
 * `speed > 0` = move mais devagar (se afasta). `speed < 0` = se aproxima.
 *
 * A implementação usa `useScroll` + `useTransform` do Motion — 100% GPU,
 * zero re-renders React.
 *
 * @example
 * <Parallax speed={0.4}>
 *   <img src="background.jpg" alt="" />
 * </Parallax>
 */
export function Parallax({ children, speed = 0.3, className }: ParallaxProps) {
  const ref = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Mapeia 0–1 do progresso em um deslocamento vertical.
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    shouldReduceMotion ? ['0%', '0%'] : [`${speed * -100}%`, `${speed * 100}%`],
  );

  return (
    <div ref={ref} style={{ overflow: 'hidden' }} className={className}>
      <motion.div style={{ y, willChange: 'transform' }}>{children}</motion.div>
    </div>
  );
}
